(() => {
  const ensureZoomStyles = () => {
    if (document.getElementById('ProductImageZoomStyles')) return;

    const styles = document.createElement('style');
    styles.id = 'ProductImageZoomStyles';
    styles.textContent = `
      .product-image-zoom-lens {
        position: absolute;
        z-index: 3;
        box-sizing: border-box;
        border: .1rem solid rgba(var(--color-foreground), .45);
        background: transparent;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 120ms ease, visibility 120ms ease;
        will-change: left, top;
      }
      .product-image-zoom-preview {
        position: fixed;
        z-index: 5;
        overflow: hidden;
        background-color: rgb(var(--color-background));
        background-repeat: no-repeat;
        box-shadow: 0 .3rem 1.4rem rgba(var(--color-shadow), .12);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 120ms ease, visibility 120ms ease;
        will-change: background-position;
      }
      .product-image-zoom-lens.is-visible,
      .product-image-zoom-preview.is-visible {
        opacity: 1;
        visibility: visible;
      }
      @media screen and (max-width: 749px), (hover: none), (pointer: coarse) {
        .product-image-zoom-lens,
        .product-image-zoom-preview { display: none !important; }
      }
    `;
    document.head.appendChild(styles);
  };

  const largestImageSource = (image) => {
    const candidates = image.srcset
      ? image.srcset.split(',').map((candidate) => candidate.trim().split(/\s+/)[0])
      : [];
    return candidates[candidates.length - 1] || image.currentSrc || image.src;
  };

  const renderedImageBounds = (image) => {
    const bounds = image.getBoundingClientRect();
    const naturalRatio = image.naturalWidth / image.naturalHeight;
    if (!naturalRatio || !bounds.width || !bounds.height || getComputedStyle(image).objectFit !== 'contain') {
      return bounds;
    }

    const boxRatio = bounds.width / bounds.height;
    if (naturalRatio > boxRatio) {
      const height = bounds.width / naturalRatio;
      return new DOMRect(bounds.left, bounds.top + (bounds.height - height) / 2, bounds.width, height);
    }

    const width = bounds.height * naturalRatio;
    return new DOMRect(bounds.left + (bounds.width - width) / 2, bounds.top, width, bounds.height);
  };

  const initializeImageZoom = (image, zoomRatio) => {
    if (image.dataset.magnifyReady === 'true' || image.closest('.quick-view-modal')) return;

    const opener = image.closest('.product__modal-opener--image');
    const productInfo = image.closest('product-info');
    const infoWrapper = productInfo?.querySelector('.product__info-wrapper');
    if (!opener || !productInfo || !infoWrapper) return;

    image.dataset.magnifyReady = 'true';

    const lens = document.createElement('span');
    lens.className = 'product-image-zoom-lens';
    lens.setAttribute('aria-hidden', 'true');

    const preview = document.createElement('span');
    preview.className = 'product-image-zoom-preview';
    preview.setAttribute('aria-hidden', 'true');
    productInfo.appendChild(preview);

    let active = false;
    let zoomFrame;

    const hideZoom = () => {
      window.cancelAnimationFrame(zoomFrame);
      active = false;
      lens.remove();
      lens.classList.remove('is-visible');
      preview.classList.remove('is-visible');
      if (window.activeProductImageZoomCleanup === hideZoom) window.activeProductImageZoomCleanup = undefined;
    };

    const moveZoom = (clientX, clientY) => {
      window.cancelAnimationFrame(zoomFrame);
      zoomFrame = window.requestAnimationFrame(() => {
        if (!active) return;

        const imageBounds = renderedImageBounds(image);
        if (
          clientX < imageBounds.left ||
          clientX > imageBounds.right ||
          clientY < imageBounds.top ||
          clientY > imageBounds.bottom
        ) {
          hideZoom();
          return;
        }

        const openerBounds = opener.getBoundingClientRect();
        const infoBounds = infoWrapper.getBoundingClientRect();
        const availableRightSpace = document.documentElement.clientWidth - infoBounds.left;
        const previewSize = Math.min(520, availableRightSpace, imageBounds.width, imageBounds.height * zoomRatio);
        const lensSize = previewSize / zoomRatio;
        const halfLens = lensSize / 2;
        const relativeX = Math.min(
          imageBounds.width - halfLens,
          Math.max(halfLens, clientX - imageBounds.left)
        );
        const relativeY = Math.min(
          imageBounds.height - halfLens,
          Math.max(halfLens, clientY - imageBounds.top)
        );

        if (lens.parentElement !== opener) opener.appendChild(lens);
        lens.style.width = `${lensSize}px`;
        lens.style.height = `${lensSize}px`;
        lens.style.left = `${imageBounds.left - openerBounds.left + relativeX - halfLens}px`;
        lens.style.top = `${imageBounds.top - openerBounds.top + relativeY - halfLens}px`;

        preview.style.left = `${infoBounds.left}px`;
        preview.style.top = `${imageBounds.top}px`;
        preview.style.width = `${previewSize}px`;
        preview.style.height = `${previewSize}px`;
        preview.style.backgroundImage = `url("${largestImageSource(image)}")`;
        preview.style.backgroundSize = `${imageBounds.width * zoomRatio}px ${imageBounds.height * zoomRatio}px`;
        preview.style.backgroundPosition = `${-(relativeX - halfLens) * zoomRatio}px ${
          -(relativeY - halfLens) * zoomRatio
        }px`;

        lens.classList.add('is-visible');
        preview.classList.add('is-visible');
      });
    };

    opener.addEventListener(
      'click',
      (event) => {
        if (!window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) return;

        const imageBounds = renderedImageBounds(image);
        if (
          event.clientX < imageBounds.left ||
          event.clientX > imageBounds.right ||
          event.clientY < imageBounds.top ||
          event.clientY > imageBounds.bottom
        ) return;

        event.preventDefault();
        event.stopPropagation();

        if (active) {
          hideZoom();
          return;
        }

        window.activeProductImageZoomCleanup?.();
        window.activeProductImageZoomCleanup = hideZoom;
        active = true;
        moveZoom(event.clientX, event.clientY);
      },
      true
    );

    opener.addEventListener('pointermove', (event) => {
      if (active && event.pointerType === 'mouse' && !event.buttons) moveZoom(event.clientX, event.clientY);
    });
    opener.addEventListener('pointerleave', hideZoom);
  };

  window.enableZoomOnHover = (zoomRatio = 2) => {
    ensureZoomStyles();
    document
      .querySelectorAll(
        '.image-magnify-hover, product-info[id^="MainProduct-"] .product__media-item .product__media img'
      )
      .forEach((image) => initializeImageZoom(image, zoomRatio));
  };

  window.enableZoomOnHover(2);
})();
